import { useState } from 'react';
import { Flexbox, Text } from '@morressier/ts';
import { ActionModal, LinkButton } from 'components/ProfileUIComponents';
import {
  faSpinnerThird,
  faInfoCircle,
  faSquareArrowUpRight
} from '@fortawesome/pro-regular-svg-icons';
import { KeyedMutator } from 'swr';
import { IProfile } from 'typings/profile';
import { useForm } from 'react-hook-form';
import { Loader } from '../../Research/Researchmodal/style';
import { updatePartialProfileGeneralInfo } from 'clients/internal/profileService';
import { FormContainer, SecondaryText, Radio } from '../style';
import { careerStageMapping } from '../../constants';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { logEvent } from 'utils/eventTracking';

interface ICareerStageModalProps {
  isOpen: boolean;
  onDismiss: () => void;
  defaultCareerStage?: string;
  userId: string;
  mutateProfile: KeyedMutator<IProfile>;
}
interface FormValues {
  careerStage: string;
}

const CareerStageModal = (props: ICareerStageModalProps) => {
  const { isOpen, onDismiss, defaultCareerStage, userId, mutateProfile } =
    props;
  const [showExitModal, setShowExitModal] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const closeExitModal = () => setShowExitModal(false);

  const openExitModal = () => setShowExitModal(true);
  const {
    register,
    handleSubmit,
    reset,
    getValues,
    formState: { errors, isDirty }
  } = useForm<FormValues>({
    defaultValues: {
      ...(defaultCareerStage ? { careerStage: defaultCareerStage } : {})
    }
  });
  const onClose = (data?: FormValues) => {
    closeExitModal();
    onDismiss();
    reset(data?.careerStage ? data : undefined);
  };
  const onSubmit = async (data: FormValues) => {
    /*
     * If there is no career stage present, it is coming from
     * the action of displaying missing indicators.
     * When editing, there is no option to set the career stage to empty.
     */
    if (!defaultCareerStage) {
      logEvent('PROFILE_ADD_CAREER_STAGE_MODAL_UPDATE_CAREER_STAGE_CLICKED');
    } else {
      logEvent('PROFILE_EDIT_CAREER_STAGE_UPDATE_CAREER_STAGE_CLICKED');
    }
    const payload = { careerStage: data.careerStage || '' };
    try {
      setIsLoading(true);
      await updatePartialProfileGeneralInfo(userId, payload);
      await mutateProfile();
      if (!defaultCareerStage) {
        logEvent('PROFILE_CAREER_STAGE_ADDED');
      }
      setIsLoading(false);
      onClose(data);
    } catch (err) {
      setIsLoading(false);
    }
  };

  const handleCautionaryAction = () => {
    const { careerStage } = getValues();
    if (isDirty && careerStage) {
      openExitModal();
    } else {
      onDismiss();
    }
  };

  const onClickCautionaryAction = () => {
    logEvent('PROFILE_CAREER_STAGE_MODAL_EXIT_WITHOUT_SAVING_CLICKED');
    handleCautionaryAction();
  };

  const handleOutSideModalClick = () => {
    logEvent('PROFILE_CAREER_STAGE_CLICK_OUTSIDE_MODAL');
    handleCautionaryAction();
  };

  const handleExitConfirmAction = () => {
    logEvent('PROFILE_CAREER_STAGE_EXIT_WITHOUT_SAVING_CONFIRM_CLICKED');
    onClose();
  };

  const handleExitCancelAction = () => {
    logEvent('PROFILE_CAREER_STAGE_EXIT_WITHOUT_SAVING_BACK_CLICKED');
    closeExitModal();
  };

  return (
    <ActionModal
      showExitContainer={showExitModal}
      onConfirmExitAction={handleExitConfirmAction}
      onCancelExitAction={handleExitCancelAction}
      onDismiss={handleOutSideModalClick}
      title="Select your research career stage"
      isOpen={isOpen}
      primaryAction={{
        title: 'Update career stage',
        disabled: isLoading,
        iconAfter: isLoading ? <Loader icon={faSpinnerThird} /> : null,
        onClick: handleSubmit(onSubmit)
      }}
      cautionaryAction={{
        title: 'Exit without saving',
        onClick: onClickCautionaryAction,
        type: 'button'
      }}
    >
      <Text>
        Make profile viewers aware of your research career stage and
        competencies. You can update this as your career grows.
      </Text>
      <FormContainer flexDirection="column" gap="1rem">
        <Radio {...register('careerStage')} value="first-stage">
          <div>
            <Text>{careerStageMapping['first-stage'].title}</Text>
            <SecondaryText>
              {careerStageMapping['first-stage'].radioDesc}
            </SecondaryText>
          </div>
        </Radio>
        <Radio {...register('careerStage')} value="recognised">
          <div>
            <Text>{careerStageMapping.recognised.title}</Text>
            <SecondaryText>
              {careerStageMapping.recognised.radioDesc}
            </SecondaryText>
          </div>
        </Radio>
        <Radio {...register('careerStage')} value="established">
          <div>
            <Text>{careerStageMapping.established.title}</Text>
            <SecondaryText>
              {careerStageMapping.established.radioDesc}
            </SecondaryText>
          </div>
        </Radio>
        <Radio {...register('careerStage')} value="leading">
          <div>
            <Text>{careerStageMapping.leading.title}</Text>
            <SecondaryText>
              {careerStageMapping.leading.radioDesc}
            </SecondaryText>
          </div>
        </Radio>
      </FormContainer>
      <Flexbox gap="8px" alignItems="center">
        <FontAwesomeIcon icon={faInfoCircle} />
        <Text>
          Morressier uses the European Commission’s Research Career Framework to
          classify career stages.
        </Text>
        <LinkButton
          as="a"
          target="_blank"
          rel="noopener noreferrer"
          href="https://euraxess.ec.europa.eu/europe/career-development/training-researchers/research-profiles-descriptors"
        >
          Learn more
          <FontAwesomeIcon icon={faSquareArrowUpRight} />
        </LinkButton>
      </Flexbox>
    </ActionModal>
  );
};
export default CareerStageModal;
